import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

export default function AdminLayout() {
  const location = useLocation();
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const [planInfo, setPlanInfo] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/admin/me/plan').then((r) => setPlanInfo(r.data)).catch(() => setPlanInfo(null));
    }
  }, [isAuthenticated]);

  // Fermer le menu mobile quand on change de page
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const isLoginPage = location.pathname === '/admin/login';

  if (isLoginPage) {
    if (isAuthenticated) {
      return <Navigate to="/admin/surveys" replace />;
    }
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const navLinkClass = (active) =>
    `block px-4 py-3 min-h-[44px] flex items-center rounded-lg ${
      active ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'
    }`;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          aria-label="Fermer le menu"
        />
      )}

      {/* Bouton menu burger (mobile) */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500"
          aria-expanded={sidebarOpen}
          aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            {sidebarOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
        <span className="font-bold text-primary-600">Baromètre Admin</span>
        <div className="w-[44px]" />
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50 w-64 bg-white shadow-sm flex flex-col
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        aria-label="Menu de navigation"
      >
        <div className="p-6 border-b pt-16 md:pt-6">
          <h2 className="font-bold text-primary-600">Baromètre Admin</h2>
          {user && <p className="text-sm text-gray-600 mt-1 truncate">{user.name}</p>}
          {planInfo?.plan && (
            <span
              className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full ${
                planInfo.plan.slug === 'free' ? 'bg-amber-100 text-amber-800' : 'bg-primary-100 text-primary-700'
              }`}
            >
              {planInfo.plan.slug === 'free' ? 'Plan gratuit' : 'Abonné'}
            </span>
          )}
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <Link to="/admin/surveys" className={navLinkClass(location.pathname.includes('surveys'))}>
            Sondages
          </Link>
          <Link to="/admin/reports" className={navLinkClass(location.pathname.includes('reports'))}>
            Rapports
          </Link>
          <Link to="/admin/alerts" className={navLinkClass(location.pathname.includes('alerts'))}>
            Alertes
          </Link>
          <Link to="/admin/subscription" className={navLinkClass(location.pathname.includes('subscription'))}>
            Mon abonnement
          </Link>
        </nav>
        <div className="p-4 border-t">
          <button
            type="button"
            onClick={logout}
            className="w-full px-4 py-3 min-h-[44px] text-left text-gray-600 hover:bg-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            Déconnexion
          </button>
        </div>
      </aside>

      <main id="main-content" className="flex-1 p-4 md:p-8 pt-20 md:pt-8 overflow-auto" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
